import React from 'react';
import { colors, fonts, radius, transitions } from '../../theme';
import { METRICS, formatChange, MUSCLE_KINDS } from '../../utils/bodyStats';

// Small shared pieces of the Body stats screens (docs/design/body-stats/v2/SPEC.md, "Tokens and exact
// component geometry"). Numbers are always in the mono face with tabular figures.

export const mono = { fontFamily: fonts.mono, fontVariantNumeric: 'tabular-nums' };

export const fmtDay = (isoDay, withYear = false) =>
  new Date(`${String(isoDay).slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
export const fmtNum = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toFixed(1));

const focusRing = { outlineOffset: 3 };

export function MetricTile({ metric, value, change, notRecorded, selected, onSelect, size = 'phone', note }) {
  const m = METRICS[metric];
  const big = size === 'desktop';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`${m.label}: ${value === null || value === undefined ? 'no reading' : `${fmtNum(value)} ${m.unitName}`}${change !== null && change !== undefined ? `, ${formatChange(change, metric)}` : ''}. Show its chart.`}
      style={{
        position: 'relative', minWidth: 0, textAlign: 'left', cursor: 'pointer',
        background: selected ? `linear-gradient(${colors.gold}0D, ${colors.gold}0D), ${colors.surface1}` : colors.surface1, border: `1.5px solid ${selected ? colors.gold : colors.border}`, borderRadius: radius.card, minHeight: 44, boxShadow: selected ? `0 0 18px ${colors.gold}18` : undefined,
        padding: big ? 16 : '12px 8px 12px', color: colors.text, fontFamily: fonts.body,
        transition: transitions.buttonPress, ...focusRing,
      }}
    >
      <div style={{ fontSize: big ? 11 : 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted }}>
        {m.label}
      </div>
      <div style={{
        ...mono, fontWeight: 700, lineHeight: 1, marginTop: 12, letterSpacing: big ? 0 : '-0.04em',
        fontSize: big ? 40 : 'clamp(24px, 7.47vw, 28px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip',
      }}>
        {fmtNum(value)}
      </div>
      <div style={{ ...mono, fontSize: 11, color: colors.textMuted, marginTop: 4 }}>{m.unit}</div>
      <div style={{ ...mono, fontSize: 11, color: colors.text, marginTop: 12, minHeight: 15 }}>
        {notRecorded ? 'Not recorded' : formatChange(change, metric) ?? ''}
      </div>
      {note && <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 4 }}>{note}</div>}
    </button>
  );
}

export function Button({ kind = 'secondary', children, style, ...rest }) {
  const base = {
    minHeight: 44, borderRadius: radius.sm, cursor: rest.disabled ? 'default' : 'pointer', fontFamily: fonts.body,
    fontSize: 15, fontWeight: 600, padding: '0 16px', transition: transitions.buttonPress, opacity: rest.disabled ? 0.5 : 1,
  };
  const kinds = {
    primary: { background: colors.gold, color: colors.bg, border: 'none', minHeight: 48 },
    secondary: { background: colors.surface2, color: colors.text, border: `1px solid ${colors.borderStrong}` },
    quiet: { background: 'transparent', color: colors.text, border: 'none' },
  };
  return <button type="button" {...rest} style={{ ...base, ...kinds[kind], ...style }}>{children}</button>;
}

export function Notice({ children, compact }) {
  return (
    <div role="note" style={{
      background: colors.surface1, border: `1px solid ${colors.borderStrong}`, borderRadius: radius.card,
      padding: compact ? 12 : 16, fontSize: 12, lineHeight: 1.5, color: colors.text,
    }}>{children}</div>
  );
}

export const muscleKindLabel = (kind) => (kind ? MUSCLE_KINDS[kind] ?? kind : null);

// A dialog over the screen: Escape closes; the first field gets focus; scrolls when tall
export function Dialog({ title, onClose, children, width = 375 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const first = ref.current?.querySelector('input, select, button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width, maxWidth: 'calc(100vw - 16px)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
          background: colors.bg, border: `1px solid ${colors.borderStrong}`, borderRadius: radius.modal,
          boxShadow: '0 12px 32px rgba(0,0,0,0.5)', padding: '16px 16px 24px', color: colors.text, fontFamily: fonts.body,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ width: 44, height: 44, background: 'transparent', border: 'none', color: colors.textMuted, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
